import { parseStoredLoginData, type AuthApiLoginData } from './auth-session';

export const AUTH_SESSION_STORAGE_KEY = 'ai-canvas-client:user-session:v1';
export const VISITOR_ID_STORAGE_KEY = 'ai-canvas-client:visitor-id:v1';
export const AUTH_RETURN_TO_STORAGE_KEY = 'ai-canvas-client:auth-return-to:v1';

function readLocal(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function removeLocal(key: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function createVisitorId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `visitor-${Math.random().toString(36).slice(2, 12)}`;
}

export function getOrCreateVisitorId() {
  const existing = readLocal(VISITOR_ID_STORAGE_KEY)?.trim();
  if (existing) return existing;
  const created = createVisitorId();
  writeLocal(VISITOR_ID_STORAGE_KEY, created);
  return created;
}

export function readStoredSession(): AuthApiLoginData | null {
  const raw = readLocal(AUTH_SESSION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = parseStoredLoginData(JSON.parse(raw));
    if (!parsed) {
      removeLocal(AUTH_SESSION_STORAGE_KEY);
    }
    return parsed;
  } catch {
    removeLocal(AUTH_SESSION_STORAGE_KEY);
    return null;
  }
}

export function writeStoredSession(loginData: AuthApiLoginData) {
  writeLocal(AUTH_SESSION_STORAGE_KEY, JSON.stringify(loginData));
}

export function clearStoredSession() {
  removeLocal(AUTH_SESSION_STORAGE_KEY);
}

export function readAuthReturnTo() {
  const raw = readLocal(AUTH_RETURN_TO_STORAGE_KEY);
  return raw?.trim() ? raw : null;
}

export function writeAuthReturnTo(target: string) {
  writeLocal(AUTH_RETURN_TO_STORAGE_KEY, target);
}

export function clearAuthReturnTo() {
  removeLocal(AUTH_RETURN_TO_STORAGE_KEY);
}
